import { useState, type ChangeEvent } from "react";

import {
  formatMoney,
  formatMoneyInput,
  formatQuantity,
  parseMoneyInput,
  parseQuantityInput,
} from "./money";
import type { QuotationLineItem } from "./model";
import { calculateLineAmountMinor } from "./totals";

interface LineItemEditorProps {
  lineItem: QuotationLineItem;
  currency: string;
  currencyMinorUnits: number;
  onChange: (lineItem: QuotationLineItem) => void;
  onDelete: () => void;
}

export function LineItemEditor({
  lineItem,
  currency,
  currencyMinorUnits,
  onChange,
  onDelete,
}: LineItemEditorProps) {
  const amount = calculateLineAmountMinor(lineItem);
  const priceLocked = lineItem.provenance.kind === "needs-review";
  const [quantityDraft, setQuantityDraft] = useState(() =>
    formatQuantity(lineItem.quantityMilli),
  );
  const [priceDraft, setPriceDraft] = useState(() =>
    formatMoneyInput(lineItem.unitPriceMinor, currencyMinorUnits),
  );
  const updateText =
    (field: "description" | "details" | "unit") =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ ...lineItem, [field]: event.target.value });
    };

  const updateQuantity = (event: ChangeEvent<HTMLInputElement>) => {
    setQuantityDraft(event.target.value);
    onChange({
      ...lineItem,
      quantityMilli: parseQuantityInput(event.target.value) ?? 0,
    });
  };

  const updatePrice = (event: ChangeEvent<HTMLInputElement>) => {
    setPriceDraft(event.target.value);
    const value = parseMoneyInput(event.target.value, currencyMinorUnits);
    onChange({
      ...lineItem,
      unitPriceMinor: value,
      valueStatus: "known",
    });
  };

  const updateTbd = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setPriceDraft("");
    }
    onChange({
      ...lineItem,
      valueStatus: event.target.checked ? "tbd" : "known",
      unitPriceMinor: event.target.checked ? null : lineItem.unitPriceMinor,
    });
  };

  const updateOptional = (event: ChangeEvent<HTMLInputElement>) => {
    const optional = event.target.checked;
    onChange({
      ...lineItem,
      optional,
      included: optional ? lineItem.included : true,
    });
  };

  return (
    <fieldset
      className="quotation-line-card"
      id={`line-${lineItem.id}`}
      data-testid={`line-${lineItem.id}`}
    >
      <legend className="quotation-sr-only">
        {lineItem.description || "New quotation item"}
      </legend>

      <div className="quotation-line-card__heading">
        <span
          className={`quotation-price-label quotation-price-label--${lineItem.provenance.kind}`}
        >
          {lineItem.provenance.label}
        </span>
        <button
          className="quotation-button quotation-button--quiet quotation-button--danger"
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${lineItem.description || "new item"}`}
        >
          Delete
        </button>
      </div>

      {lineItem.provenance.kind === "needs-review" ? (
        <p className="quotation-line-card__review-note">{lineItem.provenance.note}</p>
      ) : null}

      <div className="quotation-field quotation-field--wide">
        <label htmlFor={`description-${lineItem.id}`}>Item description</label>
        <input
          id={`description-${lineItem.id}`}
          value={lineItem.description}
          onChange={updateText("description")}
          placeholder="Product or service"
        />
      </div>

      <div className="quotation-field quotation-field--wide">
        <label htmlFor={`details-${lineItem.id}`}>Details (optional)</label>
        <textarea
          id={`details-${lineItem.id}`}
          value={lineItem.details}
          onChange={updateText("details")}
          rows={2}
          placeholder="Scope, specification, or coverage"
        />
      </div>

      <div className="quotation-line-card__grid">
        <div className="quotation-field">
          <label htmlFor={`quantity-${lineItem.id}`}>Quantity</label>
          <input
            id={`quantity-${lineItem.id}`}
            inputMode="decimal"
            value={quantityDraft}
            onChange={updateQuantity}
            aria-invalid={lineItem.quantityMilli <= 0}
          />
        </div>

        <div className="quotation-field">
          <label htmlFor={`unit-${lineItem.id}`}>Unit</label>
          <input
            id={`unit-${lineItem.id}`}
            value={lineItem.unit}
            onChange={updateText("unit")}
            placeholder="item"
          />
        </div>

        <div className="quotation-field">
          <label htmlFor={`price-${lineItem.id}`}>Unit price ({currency})</label>
          <input
            id={`price-${lineItem.id}`}
            inputMode="decimal"
            value={priceDraft}
            onChange={updatePrice}
            disabled={priceLocked || lineItem.valueStatus === "tbd"}
            placeholder={priceLocked ? "Review required" : "0.00"}
          />
        </div>

        <div className="quotation-line-card__amount" aria-live="polite">
          <span>Amount</span>
          <strong>
            {amount === null
              ? "TBD"
              : formatMoney(amount, currency, currencyMinorUnits)}
          </strong>
        </div>
      </div>

      <div className="quotation-checkbox-row">
        <label>
          <input
            type="checkbox"
            checked={lineItem.valueStatus === "tbd"}
            onChange={updateTbd}
            disabled={priceLocked}
          />
          Price is TBD
        </label>
        <label>
          <input
            type="checkbox"
            checked={lineItem.optional}
            onChange={updateOptional}
          />
          Optional item
        </label>
        {lineItem.optional ? (
          <label>
            <input
              type="checkbox"
              checked={lineItem.included}
              onChange={(event) => {
                onChange({ ...lineItem, included: event.target.checked });
              }}
            />
            Include in total
          </label>
        ) : null}
      </div>
    </fieldset>
  );
}
